import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import PendingCountBadge from './PendingCountBadge';
import ChangePasswordModal from './ChangePasswordModal';
import NoTranslate from './NoTranslate';
import './Navbar.css';

function Navbar() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [pendingCount, setPendingCount] = useState(0);
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);

  useEffect(() => {
    const fetchPendingCount = async () => {
      if (!user?.id) return;

      try {
        if (user?.is_approver && !user?.is_admin) {
          const response = await api.get(`/applications/pending-approvals/${user.id}`);
          setPendingCount(response.data?.length || 0);
        } else {
          const response = await api.get('/applications/my-applications?status=pending');
          setPendingCount(response.data?.length || 0);
        }
      } catch (error) {
        console.error('Error fetching pending count:', error);
        setPendingCount(0);
      }
    };

    fetchPendingCount();
    // Update periodically (every 30 seconds)
    const interval = setInterval(fetchPendingCount, 30000);
    return () => clearInterval(interval);
  }, [user?.is_approver, user?.is_admin, user?.id]);

  const handleLogout = () => {
    logout();
  };

  const isActive = (path) => {
    return location.pathname === path ? 'nav-link active' : 'nav-link';
  };

  return (
    <nav className="navbar">
      <div className="container nav-container">
        <div className="nav-container-top">
          <Link to="/" className="navbar-brand">
            <img 
              src="https://ais.arches-global.com/assets/logo-CyEi_NUO.svg" 
              alt="Arches Logo" 
              className="navbar-logo" 
            />
            <span className="navbar-title">Arches Attendance Management</span>
          </Link>
          
          <div className="nav-user">
            <span 
              className="user-info clickable" 
              onClick={() => setIsPasswordModalOpen(true)}
              title="Change Password"
            >
              <span className="user-icon-wrapper">
                <svg 
                  className="user-icon" 
                  width="16" 
                  height="16" 
                  viewBox="0 0 16 16" 
                  fill="currentColor"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path d="M8 8a3 3 0 1 0 0-6 3 3 0 0 0 0 6zm2-3a2 2 0 1 1-4 0 2 2 0 0 1 4 0zm4 8c0 1-1 1-1 1H3s-1 0-1-1 1-4 6-4 6 3 6 4zm-1-.004c-.001-.246-.154-.986-.832-1.664C11.516 10.68 10.289 10 8 10c-2.29 0-3.516.68-4.168 1.332-.678.678-.83 1.418-.832 1.664h10z"/>
                </svg>
              </span>
              <NoTranslate>
                {user?.full_name} ({user?.department})
              </NoTranslate>
            </span>
            <button onClick={handleLogout} className="btn btn-secondary">
              Logout
            </button>
          </div>
        </div>
        
        <div className="nav-menu">
          <div className="user-menu-section">
            <Link to="/" className={isActive('/')}>
              Dashboard
            </Link>
            <Link to="/apply" className={isActive('/apply')}>
              Leave Application
            </Link>
            <Link to="/my-applications" className={isActive('/my-applications')}>
              My Applications
              <PendingCountBadge count={pendingCount} />
            </Link>
          </div>
          
          {/* For approvers only (not admins): APPROVER Application Management */}
          {user && user.is_approver ? (
            <div className="approver-section">
              <span className="approver-label">APPROVER</span>
              <Link to="/approver" className={isActive('/approver')}>
                Application Management
              </Link>
            </div>
          ) : null}
          
          {/* For admins (including approver-admins): ADMIN section */}
          {user && user.is_admin ? (
            <div className="admin-menu">
              <div className="admin-section">
                <span className="admin-label">ADMIN</span>
                <Link to="/admin" className={isActive('/admin')}>
                  Admin Dashboard
                </Link>
                <Link to="/admin/applications" className={isActive('/admin/applications')}>
                  Application Management
                </Link>
                <Link to="/admin/leave-balance" className={isActive('/admin/leave-balance')}>
                  Paid Leave Balance Management
                </Link>
                <Link to="/admin/users" className={isActive('/admin/users')}>
                  User Management
                </Link>
                <Link to="/admin/setting" className={isActive('/admin/setting')}>
                  Settings
                </Link>
              </div>
            </div>
          ) : null}
        </div>
      </div>
      <ChangePasswordModal 
        isOpen={isPasswordModalOpen} 
        onClose={() => setIsPasswordModalOpen(false)} 
      />
    </nav>
  );
}

export default Navbar;